import * as React from "react";
import { Box, Typography, useMediaQuery, useTheme } from "@mui/material";
import "../../../App.css";

const imgBase = process.env.PUBLIC_URL + "/MyRoutesHelp_images";

const HelpImage = ({ src, alt }) => (
  <Box sx={{ my: 2, textAlign: "center" }}>
    <img
      src={`${imgBase}/${src}`}
      alt={alt}
      style={{
        maxWidth: 700,
        width: "100%",
        height: "auto",
        border: "1px solid #ccc",
        borderRadius: 4,
      }}
    />
    <Typography
      variant="caption"
      display="block"
      color="text.secondary"
      sx={{ mt: 0.5 }}
    >
      {alt}
    </Typography>
  </Box>
);

const Component = () => {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down("md"));
  return (
    <div>
      <Typography
        variant="h6"
        className="history-header"
        sx={{ paddingTop: 2, paddingLeft: 1 }}
      >
        My Routes Help
      </Typography>
      <Box
        sx={{
          paddingTop: 1,
          width: !isMobile ? "95%" : "350px",
          display: !isMobile ? "block" : "inline-block",
        }}
      >
        <Typography component="div" sx={{ paddingLeft: 1 }}>
          {/* ===== INTRODUCTION ===== */}
          <b className="history-header">Introduction</b>
          <Typography fontSize={13}>
            <p>
              Travel Midwest My Routes lets you plan driving routes on a map,
              view real-time traffic conditions along those routes, and receive
              customizable email alerts about congestion, incidents, and
              construction. The system is free to use.
            </p>
            <p>My Routes consists of two main pages:</p>
            <ul>
              <li>
                <b>My Routes</b> (the route planner) &mdash; plan routes by
                entering start and destination addresses, view real-time traffic
                along the route, customize the route with waypoints, save routes
                to your account, and configure email alerts.
              </li>
              <li>
                <b>My Account</b> &mdash; manage your profile, change your
                password, set vacation mode to pause alerts, and view or delete
                your saved routes.
              </li>
            </ul>
            <p>
              You can access both pages from the <b>My Routes</b> menu.
            </p>
          </Typography>

          {/* ===== GETTING STARTED ===== */}
          <b className="history-header">
            Getting Started &mdash; Creating an Account
          </b>
          <Typography fontSize={13}>
            <p>
              You do not need an account to plan routes and view traffic
              information, but you do need one to save routes and receive email
              alerts. To create an account:
            </p>
            <ol>
              <li>
                Open the <b>My Routes</b> page and click{" "}
                <b>Log In / Register</b> in the upper right, or save a route
                (you will be prompted to log in or register).
              </li>
              <li>
                Select the <b>Register</b> tab.
              </li>
              <li>
                Enter your email address and choose a password. Passwords must
                be at least 8 characters long.
              </li>
              <li>
                Select your preferred email format: <b>HTML</b> (formatted) or{" "}
                <b>Plain Text</b>.
              </li>
              <li>
                Click <b>Register</b>.
              </li>
              <li>
                Check your email for a verification message and click the link
                to activate your account. If you do not see it, check your spam
                folder.
              </li>
            </ol>
            <p>
              Once verified, you can log in from the My Routes page to access
              your saved routes and alert settings.
            </p>
            <HelpImage
              src="register.png"
              alt="The registration form on the My Routes page"
            />
          </Typography>

          {/* ===== PLANNING A ROUTE ===== */}
          <b className="history-header">Planning a Route</b>
          <Typography fontSize={13}>
            <p>
              The My Routes page displays a map of the Travel Midwest coverage
              area with real-time traffic conditions. To plan a route:
            </p>
            <ol>
              <li>
                Enter a <b>start location</b> in the first address field. As you
                type, suggestions will appear &mdash; select one to set your
                starting point.
              </li>
              <li>
                Enter a <b>destination</b> in the second address field and
                select a suggestion.
              </li>
              <li>
                The system calculates the route automatically and displays it on
                the map with real-time traffic coloring.
              </li>
            </ol>
            <HelpImage
              src="route-planning.png"
              alt="Entering start and destination to plan a route"
            />
          </Typography>

          {/* ===== CUSTOMIZING A ROUTE ===== */}
          <b className="history-header">Customizing a Route with Waypoints</b>
          <Typography fontSize={13}>
            <p>
              If you want the route to follow a specific path, you can add
              waypoints:
            </p>
            <ol>
              <li>
                Click the <b>Customize</b> button to enter customization mode.
              </li>
              <li>
                Click on the map to add waypoints along the desired path. The
                route recalculates through each waypoint.
              </li>
              <li>Drag a waypoint to reposition it.</li>
              <li>Click or right-click a waypoint to remove it.</li>
              <li>
                Click <b>Done</b> when you are satisfied with the route.
              </li>
            </ol>
            <HelpImage
              src="customize-waypoints.png"
              alt="Adding waypoints to customize a route"
            />
          </Typography>

          {/* ===== VIEWING TRAFFIC ===== */}
          <b className="history-header">Viewing Traffic Information</b>
          <Typography fontSize={13}>
            <p>
              Once a route is displayed on the map, you can see real-time
              traffic conditions:
            </p>
            <ul>
              <li>
                <b>Congestion coloring</b> on the route line shows current
                traffic speed (green for free-flowing, yellow for moderate, red
                for heavy congestion).
              </li>
              <li>
                <b>Incident markers</b> show accidents, hazards, and road
                closures along the route.
              </li>
              <li>
                <b>Construction markers</b> show active roadwork.
              </li>
              <li>
                <b>Camera markers</b> show traffic camera locations &mdash;
                click to view the live image.
              </li>
            </ul>
            <p>
              A route information panel displays the total travel time,
              distance, average speed, congestion level, and counts of incidents
              and construction along the route.
            </p>
            <HelpImage
              src="traffic-info.png"
              alt="Route with real-time traffic information and route details panel"
            />
          </Typography>

          {/* ===== SAVING A ROUTE ===== */}
          <b className="history-header">Saving a Route</b>
          <Typography fontSize={13}>
            <p>
              To save a route so you can view it later and optionally receive
              email alerts:
            </p>
            <ol>
              <li>Plan your route as described above.</li>
              <li>
                Click <b>Save Route</b>.
              </li>
              <li>
                If you are not logged in, a login/register dialog appears. Log
                in or create an account.
              </li>
              <li>
                After logging in, the email alert configuration dialog appears
                where you can set up alerts for this route (see below). You can
                also choose not to receive alerts.
              </li>
              <li>
                Click <b>Save Configuration</b> to save the route and alert
                settings.
              </li>
            </ol>
            <p>
              Your saved routes appear in the <b>My Routes</b> panel on the left
              side of the page. Each saved route shows its name, current
              congestion level, and traffic statistics.
            </p>
            <HelpImage
              src="my-routes-panel.png"
              alt="The My Routes panel showing saved routes with traffic summaries"
            />
          </Typography>

          {/* ===== MANAGING SAVED ROUTES ===== */}
          <b className="history-header">Managing Saved Routes</b>
          <Typography fontSize={13}>
            <p>From the My Routes panel, you can:</p>
            <ul>
              <li>
                <b>View a route</b> &mdash; click on a saved route to display it
                on the map with current traffic conditions.
              </li>
              <li>
                <b>Rename a route</b> &mdash; click the route name to edit it
                inline.
              </li>
              <li>
                <b>Configure alerts</b> &mdash; click the bell icon to open the
                alert settings for that route.
              </li>
              <li>
                <b>Delete a route</b> &mdash; click the trash icon and confirm
                deletion.
              </li>
            </ul>
            <p>
              Expand a route to see a detailed breakdown of incidents and
              construction along the route.
            </p>
            <HelpImage
              src="route-details-expanded.png"
              alt="Expanded route showing incident and construction details"
            />
          </Typography>

          {/* ===== EMAIL ALERTS ===== */}
          <b className="history-header">Setting Up Email Alerts</b>
          <Typography fontSize={13}>
            <p>
              Email alerts notify you of traffic conditions along your saved
              routes. To configure alerts for a route, click the bell icon next
              to it in the My Routes panel (or configure alerts when saving a
              new route). The alert configuration dialog lets you set:
            </p>
            <HelpImage
              src="alert-config.png"
              alt="The email alert configuration dialog"
            />

            <p>
              <b>Email Frequency</b>
            </p>
            <ul>
              <li>
                <b>No emails</b> &mdash; save the route without receiving alerts
              </li>
              <li>
                <b>Once per day</b> &mdash; one email per day if conditions meet
                your criteria
              </li>
              <li>
                <b>Every 15 minutes</b>, <b>Every 30 minutes</b>, or{" "}
                <b>Every hour</b> &mdash; periodic checks during your specified
                time window
              </li>
            </ul>

            <p>
              <b>Time Window</b>
            </p>
            <p>
              Set the start and end times during which you want to receive
              alerts (e.g., 7:00 AM to 9:00 AM for your morning commute). Alerts
              are only sent during this window.
            </p>

            <p>
              <b>Days of Week</b>
            </p>
            <p>
              Select which days of the week to receive alerts using the
              day-of-week buttons (M, T, W, Th, F, Sa, Su).
            </p>

            <p>
              <b>Reports to Include</b>
            </p>
            <ul>
              <li>
                <b>Travel Time</b> &mdash; estimated travel time along the route
              </li>
              <li>
                <b>Congestion</b> &mdash; traffic speed and congestion levels
              </li>
              <li>
                <b>Construction</b> &mdash; active roadwork and closures
              </li>
              <li>
                <b>Incidents</b> &mdash; accidents, hazards, and closures
              </li>
            </ul>

            <p>
              <b>Speed Threshold</b>
            </p>
            <p>
              Set a speed threshold (5&ndash;70 mph) so that alerts are only
              sent when average speed along the route falls below that value.
              You can also disable the threshold to receive alerts regardless of
              speed.
            </p>

            <p>
              <b>Severe Incidents</b>
            </p>
            <p>
              You can choose to receive alerts for all severe incidents along
              your route, even outside your normal time window. If you enable
              this option, you can also set <b>quiet hours</b> (e.g., 10:00 PM
              to 6:00 AM) during which severe incident alerts are suppressed.
            </p>

            <p>
              <b>Send Now</b>
            </p>
            <p>
              After saving alert settings, you can click <b>Send Now</b>{" "}
              (visible in the lower left of the alert configuration dialog) to
              immediately send a test alert email for the route. This lets you
              verify your settings and see what the alert looks like. Your email
              must be verified to use this feature.
            </p>
          </Typography>

          {/* ===== MY ACCOUNT ===== */}
          <b className="history-header">My Account</b>
          <Typography fontSize={13}>
            <p>
              The My Account page lets you manage your account settings. Access
              it from the <b>My Routes</b> menu. The page has the following
              sections:
            </p>

            <p>
              <b>Profile</b>
            </p>
            <ul>
              <li>Update your email address.</li>
              <li>Choose your email format (HTML or Plain Text).</li>
              <li>
                View your email verification status. If your email is not
                verified, click <b>Resend verification email</b> to receive a
                new verification link.
              </li>
            </ul>
            <HelpImage
              src="my-account-profile.png"
              alt="The My Account profile section"
            />

            <p>
              <b>Change Password</b>
            </p>
            <p>
              Enter a new password and confirm it. The system shows password
              strength requirements as you type (minimum 8 characters).
            </p>

            <p>
              <b>Vacation Mode</b>
            </p>
            <p>
              If you will be away and do not want to receive alerts, set a
              vacation start and end date. All email alerts are suspended during
              this period. Click <b>Clear</b> to remove the vacation dates and
              resume alerts.
            </p>
            <HelpImage
              src="my-account-vacation.png"
              alt="The vacation mode date picker"
            />

            <p>
              <b>My Routes</b>
            </p>
            <p>
              This section shows all your saved routes. You can rename or delete
              routes from here. To edit a route's path or alert settings, go to
              the My Routes page.
            </p>

            <p>
              <b>Delete Account</b>
            </p>
            <p>
              At the bottom of the page, you can permanently delete your account
              and all saved routes. This action cannot be undone.
            </p>
          </Typography>

          {/* ===== PASSWORD RESET ===== */}
          <b className="history-header">Resetting Your Password</b>
          <Typography fontSize={13}>
            <p>If you have forgotten your password:</p>
            <ol>
              <li>
                Click <b>Log In / Register</b> on the My Routes page.
              </li>
              <li>
                On the Login tab, click <b>Forgot password?</b>
              </li>
              <li>
                Enter your email address and click <b>Send Reset Link</b>.
              </li>
              <li>Check your email for a password reset link and click it.</li>
              <li>Enter your new password on the reset page.</li>
            </ol>
            <p>
              You can also change your password at any time from the My Account
              page.
            </p>
          </Typography>

          {/* ===== TIPS ===== */}
          <b className="history-header">Tips</b>
          <Typography fontSize={13}>
            <ul>
              <li>
                You must verify your email address before you can receive alert
                emails or use the Send Now feature.
              </li>
              <li>
                You can save routes without setting up alerts &mdash; just
                choose "No emails" as the frequency. This lets you quickly view
                traffic conditions on the route whenever you visit the site.
              </li>
              <li>
                Use vacation mode to temporarily pause all alerts instead of
                deleting your alert settings.
              </li>
              <li>
                Click on camera markers along your route to view live traffic
                camera images.
              </li>
              <li>
                Expand a saved route in the My Routes panel to see a detailed
                breakdown of current incidents and construction.
              </li>
            </ul>
          </Typography>
        </Typography>
      </Box>
    </div>
  );
};

const module = {
  routeProps: {
    path: "MyRoutesHelp",
    component: Component,
  },

  name: "My Routes Help",
  label: "My Routes Help",
  title: "My Routes Help",
  showOnMenu: true,
};

export default module;
